import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Send, Loader2, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type Message = { role: "user" | "assistant"; content: string };

const MessageContent = ({ content }: { content: string }) => {
  const navigate = useNavigate();

  // Parse markdown links [text](url) and render as buttons
  const parts = content.split(/(\[.*?\]\(.*?\))/g);

  return (
    <>
      {parts.map((part, index) => {
        const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
        if (linkMatch) {
          const [, text, url] = linkMatch;
          return (
            <Button key={index} onClick={() => navigate(url)} className="ml-2 mt-2" size="sm">
              {text}
            </Button>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
};

const StrategyCheck = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"questionnaire" | "conversation" | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const startQuestionnaire = async () => {
    setMode("questionnaire");

    // Create session in database
    const { data: session } = await supabase.from("chat_sessions").insert({ mode: "questionnaire" }).select().single();

    if (session) setSessionId(session.id);

    const initialMessage = {
      role: "assistant" as const,
      content:
        "Great! I'll ask you 5 quick multiple choice questions to assess if your company is fit for purpose.\n\n**Question 1 of 5 - Organizational Strategy:**\nWhat is the strategy of your organization?\n\nA) To become adaptive\nB) To deliver as fast as possible\nC) To utilize resources optimally\nD) We don't have a strategy\n\nPlease respond with just the letter (A, B, C, or D).",
    };

    setMessages([initialMessage]);

    // Save initial message to database
    if (session) {
      await supabase.from("chat_messages").insert({
        session_id: session.id,
        role: "assistant",
        content: initialMessage.content,
      });
    }
  };

  const startConversation = async () => {
    setMode("conversation");

    // Create session in database
    const { data: session } = await supabase.from("chat_sessions").insert({ mode: "conversation" }).select().single();

    if (session) setSessionId(session.id);

    const initialMessage = {
      role: "assistant" as const,
      content:
        "Hello! I'm your Org Topologies™ consultant. Let's have a conversation about your organization. Tell me a bit about your company - what do you do, and what are some of the challenges you're currently facing?",
    };

    setMessages([initialMessage]);

    // Save initial message to database
    if (session) {
      await supabase.from("chat_messages").insert({
        session_id: session.id,
        role: "assistant",
        content: initialMessage.content,
      });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading || !mode || !sessionId) return;

    // Client-side validation
    const trimmedInput = input.trim();
    if (trimmedInput.length === 0) {
      toast({
        title: "Empty message",
        description: "Please enter a message before sending.",
        variant: "destructive",
      });
      return;
    }

    if (trimmedInput.length > 2000) {
      toast({
        title: "Message too long",
        description: "Please keep your message under 2000 characters.",
        variant: "destructive",
      });
      return;
    }

    const userMessage: Message = { role: "user", content: trimmedInput };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Save user message to database
    if (sessionId) {
      await supabase.from("chat_messages").insert({
        session_id: sessionId,
        role: "user",
        content: userMessage.content,
      });
    }

    let assistantContent = "";
    const updateAssistant = (chunk: string) => {
      assistantContent += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantContent } : m));
        }
        return [...prev, { role: "assistant", content: assistantContent }];
      });
    };

    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/strategy-chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          mode,
          sessionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const isRateLimit = response.status === 429 || errorData.error?.includes("Too many requests");

        if (isRateLimit) {
          toast({
            title: "Rate limit exceeded",
            description: "You're sending messages too quickly. Please wait a moment before trying again.",
            variant: "destructive",
          });
        } else {
          throw new Error(errorData.error || "Failed to get response");
        }

        setIsLoading(false);
        setMessages((prev) => prev.slice(0, -1));
        return;
      }

      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let done = false;

      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;

        if (value) {
          buffer += decoder.decode(value, { stream: true });

          let newlineIndex: number;
          while ((newlineIndex = buffer.indexOf("\n")) !== -1) {
            let line = buffer.slice(0, newlineIndex);
            buffer = buffer.slice(newlineIndex + 1);

            if (line.endsWith("\r")) line = line.slice(0, -1);
            if (line.startsWith(":") || line.trim() === "") continue;
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.slice(6).trim();
            if (jsonStr === "[DONE]") {
              done = true;
              break;
            }

            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (content) updateAssistant(content);
            } catch {
              buffer = line + "\n" + buffer;
              break;
            }
          }
        }
      }

      if (buffer.trim()) {
        for (let raw of buffer.split("\n")) {
          if (!raw || raw.startsWith(":") || raw.trim() === "") continue;
          if (!raw.startsWith("data: ")) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === "[DONE]") continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) updateAssistant(content);
          } catch {}
        }
      }

      // Save complete assistant message to database
      if (sessionId && assistantContent) {
        await supabase.from("chat_messages").insert({
          session_id: sessionId,
          role: "assistant",
          content: assistantContent,
        });
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Chat error:", error);
      }
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="fixed top-0 w-full bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-vailery text-primary">
              Orren Consultancy
            </Link>
            <Button variant="ghost" asChild>
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-32 container mx-auto px-6 max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-4 text-foreground">Free Strategy Check Tool</h1>
          <p className="text-muted-foreground text-lg">
            Discover if your organization is fit for purpose with our AI consultant
          </p>
        </div>

        {!mode ? (
          /* Mode Selection */
          <Card className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">How would you like to proceed?</h2>
              <p className="text-muted-foreground">Choose your preferred assessment method</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card
                className="p-6 border-2 hover:border-primary transition-colors cursor-pointer"
                onClick={startQuestionnaire}
              >
                <h3 className="text-xl font-semibold mb-3 text-foreground">Quick Assessment</h3>
                <p className="text-muted-foreground mb-4">Answer 5 multiple choice questions for a fast evaluation</p>
                <ul className="text-sm text-muted-foreground space-y-2 mb-6">
                  <li>• Takes 2-3 minutes</li>
                  <li>• Structured questions</li>
                  <li>• Instant results</li>
                </ul>
                <Button className="w-full">Start Questionnaire</Button>
              </Card>
              <Card
                className="p-6 border-2 hover:border-primary transition-colors cursor-pointer"
                onClick={startConversation}
              >
                <h3 className="text-xl font-semibold mb-3 text-foreground">Conversational Discovery</h3>
                <p className="text-muted-foreground mb-4">Have a natural conversation about your organization</p>
                <ul className="text-sm text-muted-foreground space-y-2 mb-6">
                  <li>• More personalized</li>
                  <li>• Flexible discussion</li>
                  <li>• Deeper insights</li>
                </ul>
                <Button className="w-full">Start Conversation</Button>
              </Card>
            </div>
          </Card>
        ) : (
          /* Chat Messages */
          <Card className="p-6 min-h-[500px] max-h-[600px] overflow-y-auto mb-4">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div key={index} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] rounded-lg p-4 ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-secondary-foreground"
                    }`}
                  >
                    <div className="whitespace-pre-wrap">
                      <MessageContent content={message.content} />
                    </div>
                  </div>
                </div>
              ))}
              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex justify-start">
                  <div className="bg-secondary text-secondary-foreground rounded-lg p-4">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </Card>
        )}

        {/* Input Area */}
        {mode && (
          <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4">
            <div className="container mx-auto max-w-4xl">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    mode === "questionnaire" ? "Type your answer (A, B, C, or D)..." : "Type your message..."
                  }
                  disabled={isLoading}
                  className="flex-1"
                />
                <Button onClick={sendMessage} disabled={isLoading || !input.trim()}>
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyCheck;
